import {ArrowLeft, X} from 'lucide-react';
import {Dialog as DialogPrimitive} from 'radix-ui';
import * as React from 'react';

import {cn} from '@/utils/ui';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({className, children, ...props}, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 w-screen overflow-y-auto glass-neutral',
      className
    )}
    {...props}
  >
    <div className="flex min-h-full flex-col items-center justify-center md:p-4">
      {children}
    </div>
  </DialogPrimitive.Overlay>
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  hasDescription?: boolean;
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({className, children, hasDescription, ...props}, ref) => (
  <DialogPortal>
    <DialogOverlay>
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'relative flex w-full flex-col gap-5 border bg-neutral-light p-4 md:p-8',
          'md:max-w-2xl md:rounded-soft md:shadow-default',
          'max-md:grow max-md:pt-0',
          className
        )}
        {...(!hasDescription && {'aria-describedby': undefined})}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogOverlay>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogSection = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex grow flex-col gap-4 py-8', className)} {...props} />
);
DialogSection.displayName = 'DialogSection';

export type DialogSectionProps = React.ComponentProps<typeof DialogSection> & {
  hideCloseButton?: boolean;
};

const DialogHeader = ({
  className,
  children,
  hideCloseButton,
  ...props
}: DialogSectionProps) => (
  <div
    className={cn(
      'flex flex-row gap-2 sm:flex-row sm:items-center sm:justify-start',
      'max-md:sticky max-md:top-0 max-md:z-50 max-md:-mx-4 max-md:border-b max-md:border-border max-md:bg-neutral-light max-md:px-4 max-md:py-3',
      className
    )}
    {...props}
  >
    {!hideCloseButton && (
      <DialogPrimitive.Close className="disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground md:hidden">
        <ArrowLeft size={18} />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    )}
    {children}
    {!hideCloseButton && (
      <DialogPrimitive.Close className="absolute top-6 right-6 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground max-md:hidden md:top-10">
        <X />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    )}
  </div>
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse gap-2 md:flex-row md:items-center md:justify-end',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({className, ...props}, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('pr-8 type-h2', 'max-md:z-20 max-md:text-xl', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({className, ...props}, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-xs text-gray-800 md:my-2 md:text-sm', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogSection,
  DialogTitle,
  DialogTrigger,
};

export type DialogProps = React.ComponentProps<typeof Dialog>;
